import React from 'react';

import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoNotDisturbOnOutlinedIcon from '@mui/icons-material/DoNotDisturbOnOutlined';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DescriptionIcon from '@mui/icons-material/Description';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Box, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import TextField from '@mui/material/TextField';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import { sortByOrder, usePersistedSetting } from '@@common';

import { ParameterMetadata, ParameterType } from '../../../api/api';
import CopyToClipboardButton from '../../../components/Clipboard/CopyToClipboardButton';
import { getNestedValue, setNestedValue } from '../Models/PropertyHelpers';
import { VariableScope } from '../Models/VariableScopes/VariableScope';
import PropertyIcon from './PropertyIcon';
import PropertyValueInput from './PropertyValueInput';
import { AGENT_TOOL_INPUT_CATEGORY, AGENT_TOOL_OUTPUT_CATEGORY, AGENT_TOOL_INPUT_SCHEMA_CATEGORY, AGENT_TOOL_OUTPUT_SCHEMA_CATEGORY } from '../Models/FlowModel';
import AgentToolRoleChip from '../Components/AgentToolRoleChip';

type GenericPropertiesProps<T extends Record<string, any>> = {
	model: T;
	onModelChanged?: (model: T) => void;
	parameters: ParameterMetadata[];
	variableScope?: VariableScope;
	isVariableScopeLoading?: boolean;
	readOnly?: boolean;
	id?: string;
	name?: React.ReactNode;
	isAgentTool?: boolean;
	agentToolInputOverrides?: Record<string, boolean | undefined>;
	agentToolBoundInputs?: string[];
	agentToolInputDescriptions?: Record<string, string>;
	onAgentToolInputOverrideChanged?: (propertyName: string, enabled: boolean | undefined) => void;
	onAgentToolInputDescriptionChanged?: (propertyName: string, description: string | undefined) => void;
	onResetAgentToolInputOverrides?: () => void;
	/// Optional custom renderer for parameter values
	children?: (parameterMetadata: ParameterMetadata, value: any, onValueChange: (value: any) => void) => React.ReactNode;
};

const DEFAULT_CATEGORY = 'General';
const ADVANCED_CATEGORY = 'Advanced';

export function GenericProperties<T extends Record<string, any>>({
	parameters,
	model,
	onModelChanged,
	variableScope,
	isVariableScopeLoading,
	readOnly,
	id,
	name,
	children,
	isAgentTool = false,
	agentToolInputOverrides,
	agentToolBoundInputs,
	agentToolInputDescriptions,
	onAgentToolInputOverrideChanged,
	onAgentToolInputDescriptionChanged,
	onResetAgentToolInputOverrides,
}: GenericPropertiesProps<T>) {
	const [showDescription, setShowDescription] = usePersistedSetting('genericProperties.showDescription', false);
	const [showToolInputDescriptions, setShowToolInputDescriptions] = React.useState<Record<string, boolean>>({});
	React.useEffect(() => {
		setShowToolInputDescriptions({});
	}, [id]);

	const filteredParams = parameters.filter((p) => p.type !== ParameterType.Activity).sort(sortByOrder);

	// Group parameters by category
	const paramsByCategory = filteredParams.reduce(
		(acc, param) => {
			const category = param.category || DEFAULT_CATEGORY;
			if (!acc[category]) {
				acc[category] = [];
			}
			acc[category].push(param);
			return acc;
		},
		{} as Record<string, ParameterMetadata[]>,
	);

	// Get section order based on first parameter's order in each category
	const sectionOrder = Object.entries(paramsByCategory)
		.map(([category, params]) => ({
			category,
			order: params[0].order,
		}))
		.sort((a, b) => {
			// Always put Advanced last
			if (a.category === ADVANCED_CATEGORY) return 1;
			if (b.category === ADVANCED_CATEGORY) return -1;
			return sortByOrder(a, b);
		})
		.map((s) => s.category);

	const renderParameter = (p: ParameterMetadata) => {
		const value = getNestedValue(model, p.name);
		const hasConfiguredValue = value !== undefined && value !== null && value !== '';
		const isAgentToolInput = isAgentTool && p.categories?.includes(AGENT_TOOL_INPUT_CATEGORY)
			&& !(p.name === 'UserPrompt' && getNestedValue(model, 'PromptSchema'));
		const isAgentToolOutput = isAgentTool && p.categories?.includes(AGENT_TOOL_OUTPUT_CATEGORY);
		const isAgentToolInputSchema = isAgentTool && p.categories?.includes(AGENT_TOOL_INPUT_SCHEMA_CATEGORY);
		const isAgentToolOutputSchema = isAgentTool && p.categories?.includes(AGENT_TOOL_OUTPUT_SCHEMA_CATEGORY);
		const inputOverride = agentToolInputOverrides?.[p.name];
		const isBoundInput = agentToolBoundInputs?.includes(p.name) === true;
		const showToolInputDescription = isAgentToolInput ? !!showToolInputDescriptions[p.name] : false;
		const inputDescriptionOverride = agentToolInputDescriptions?.[p.name];
		const inputOverrideLabel = inputOverride === undefined ? 'Agent input: default' : inputOverride ? 'Agent input: enabled' : 'Agent input: disabled';
		const InputOverrideIcon = inputOverride === undefined ? RadioButtonUncheckedIcon : inputOverride ? CheckCircleIcon : DoNotDisturbOnOutlinedIcon;
		const cycleInputOverride = () => {
			if (!onAgentToolInputOverrideChanged) return;
			const next = inputOverride === undefined ? false : inputOverride === false ? true : undefined;
			onAgentToolInputOverrideChanged(p.name, next);
		};
		const setDescription = (value: string | undefined) => {
			onAgentToolInputDescriptionChanged?.(p.name, value);
		};
		const toggleToolInputDescription = (event: React.MouseEvent) => {
			event.stopPropagation();
			setShowToolInputDescriptions((previous) => ({
				...previous,
				[p.name]: !previous[p.name],
			}));
		};

		return (
			<ListItem key={p.name} sx={{ py: 0.5 }} title={showDescription ? undefined : p.description}>
				<ListItemIcon sx={{ minWidth: 32 }}>
					<PropertyIcon parameterMetadata={p} isLoading={p.type === ParameterType.Expression && isVariableScopeLoading} />
				</ListItemIcon>
				<ListItemText
					primary={
						<Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0.5 }}>
											<Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
								{p.displayName}
								{p.isRequired && (
									<Typography component="span" color="warning" sx={{ ml: 0.5 }}>
										*
									</Typography>
								)}
							</Box>
							{(isAgentToolInput || isAgentToolOutput || isAgentToolInputSchema || isAgentToolOutputSchema) && (
								<Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.35 }}>
									{isAgentToolInput ? (
										<AgentToolRoleChip
											role="input"
											muted={isBoundInput || hasConfiguredValue || inputOverride === false}
											onClick={onAgentToolInputOverrideChanged ? cycleInputOverride : undefined}
											endAdornment={
												onAgentToolInputOverrideChanged ? (
													<Tooltip title={`${inputOverrideLabel}. Click to cycle default, disabled, enabled`}>
														<IconButton
															size="small"
															aria-label={inputOverrideLabel}
															onClick={(event) => {
																event.stopPropagation();
																cycleInputOverride();
															}}
															sx={{
																p: 0,
																ml: 0.35,
																width: 16,
																height: 16,
																flexShrink: 0,
																color: inputOverride === false ? '#ffb4ab' : inputOverride ? '#b9f6c5' : 'rgba(255,255,255,0.95)',
																'& svg': { fontSize: 14 },
																'&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' },
															}}
														>
															<InputOverrideIcon fontSize="inherit" />
												</IconButton>
											</Tooltip>
										) : undefined
											}
										/>
									) : isAgentToolInputSchema ? (
										<AgentToolRoleChip role="inputSchema" muted={!hasConfiguredValue} />
									) : isAgentToolOutputSchema ? (
										<AgentToolRoleChip role="outputSchema" muted={!hasConfiguredValue} />
									) : (
										<AgentToolRoleChip role="output" muted={!hasConfiguredValue} />
									)}
									{onAgentToolInputDescriptionChanged && isAgentToolInput && (
										<Tooltip title={showToolInputDescription ? 'Hide tool description override' : 'Show tool description override'}>
											<IconButton
												size="small"
												aria-label={showToolInputDescription ? 'Hide tool description override' : 'Show tool description override'}
												onClick={toggleToolInputDescription}
												sx={{
													p: 0,
													ml: 0.35,
													alignSelf: 'center',
													width: 16,
													height: 16,
													flexShrink: 0,
													color: 'rgba(255,255,255,0.95)',
													'&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' },
													'& svg': { fontSize: 14 },
												}}
											>
												{showToolInputDescription ? <DescriptionIcon /> : <DescriptionOutlinedIcon />}
											</IconButton>
										</Tooltip>
									)}
								</Box>
							)}
						</Box>
					}
					secondary={
						<>
							{isBoundInput && <Typography variant="caption" color="info.main">
								Flow-controlled input. Select its AgentTool transition to edit the override expression.
							</Typography>}
							<PropertyValueInput
								parameterMetadata={p}
								value={value}
								onValueChange={(newValue) => {
									if (readOnly) {
										return;
									}

									const currentValue = getNestedValue(model, p.name);
									// Only update if the value actually changed
									if (currentValue !== newValue) {
										const updatedModel = setNestedValue(model, p.name, newValue);
										onModelChanged?.(updatedModel);
									}
								}}
								variableScope={variableScope}
								parameters={parameters}
								model={model}
								readOnly={readOnly}
							>
								{children}
							</PropertyValueInput>
							{showDescription && p.description && (
								<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
									{p.description}
								</Typography>
							)}
							{showToolInputDescription && onAgentToolInputDescriptionChanged && (
								<Box sx={{ mt: 1 }}>
									<TextField
										fullWidth
										size="small"
										label="Tool field description"
										value={inputDescriptionOverride ?? ''}
										placeholder={p.description}
										onChange={(event) => {
											const value = event.target.value;
											const next = value.trim();
											onAgentToolInputDescriptionChanged(p.name, next ? next : undefined);
										}}
										onBlur={(event) => {
											const value = event.target.value;
											const next = value.trim();
											setDescription(next ? next : undefined);
										}}
										inputProps={{ style: { fontSize: '0.75rem' } }}
									/>
								</Box>
							)}
						</>
					}
					slotProps={{ secondary: { component: 'div' } }}
				/>
			</ListItem>
		);
	};

	return (
		<List dense>
			{(name || filteredParams.some((p) => p.description)) && (
				<ListItem sx={{ py: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					{name && (
						<Typography variant="subtitle1" component="div" sx={{ display: 'flex', fontWeight: 600, flex: 1, minWidth: 0 }}>
							{name}
						</Typography>
					)}
					{onResetAgentToolInputOverrides && (
						<Tooltip title="Reset agent input overrides">
							<IconButton size="small" onClick={onResetAgentToolInputOverrides} aria-label="Reset agent input overrides" sx={{ p: 0.5 }}>
								<RestartAltIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					)}
					{filteredParams.some((p) => p.description) && (
						<Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
							<Tooltip title={showDescription ? 'Hide parameter descriptions' : 'Show parameter descriptions'}>
								<IconButton
									size="small"
									onClick={() => setShowDescription(!showDescription)}
									color={showDescription ? 'primary' : 'default'}
									aria-label={showDescription ? 'Hide parameter descriptions' : 'Show parameter descriptions'}
									aria-pressed={showDescription}
									sx={{ p: 0.5 }}
								>
									<InfoIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						</Box>
					)}
				</ListItem>
			)}
			{id && (
				<ListItem sx={{ py: 0.5 }}>
					<ListItemIcon sx={{ minWidth: 32 }}>
						<InfoIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary="ID"
						secondary={
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
								<Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1 }}>
									{id}
								</Typography>
								<CopyToClipboardButton value={id} size="small" iconSize={16} />
							</Box>
						}
						slotProps={{ secondary: { component: 'div' } }}
					/>
				</ListItem>
			)}
			{sectionOrder.map((category, index) => (
				<React.Fragment key={category}>
					{category !== DEFAULT_CATEGORY && (
						<ListItem>
							<Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.secondary', py: 1 }}>
								{category}
							</Typography>
						</ListItem>
					)}
					{paramsByCategory[category].map(renderParameter)}
					{category !== DEFAULT_CATEGORY && index < sectionOrder.length - 1 && <Divider />}
				</React.Fragment>
			))}
		</List>
	);
}
